// Filterable typeahead. Used by HaInstancePicker (Task 6) and
// LogicalGroup member-add (Task 7). Substring match across the label
// extracted by `getLabel`. Submits via `onSelect(item)`; controlled
// value lives in the form state outside.

import { useMemo, useState } from "react";

interface AutocompleteProps<T> {
  items: ReadonlyArray<T>;
  value: T | null;
  onSelect: (item: T | null) => void;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getSecondary?: (item: T) => string | undefined;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
}

export function Autocomplete<T>({
  items,
  value,
  onSelect,
  getKey,
  getLabel,
  getSecondary,
  placeholder = "Start typing…",
  disabled,
  allowClear = true,
}: AutocompleteProps<T>) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => getLabel(item).toLowerCase().includes(q));
  }, [items, query, getLabel]);

  const selectedLabel = value ? getLabel(value) : "";

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        disabled={disabled}
        value={open ? query : selectedLabel}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        style={inputStyle}
      />
      {value && allowClear && !open && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-label="Clear"
          style={clearStyle}
        >
          ×
        </button>
      )}
      {open && filtered.length > 0 && (
        <ul style={listStyle} role="listbox">
          {filtered.slice(0, 20).map((item) => (
            <li
              key={getKey(item)}
              role="option"
              aria-selected={value !== null && getKey(value) === getKey(item)}
              onMouseDown={() => {
                onSelect(item);
                setOpen(false);
                setQuery("");
              }}
              style={listItemStyle}
            >
              <span>{getLabel(item)}</span>
              {getSecondary && (
                <span style={{ color: "var(--text-dim)", fontSize: "0.75rem" }}>
                  {getSecondary(item)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && filtered.length === 0 && (
        <ul style={listStyle} role="listbox">
          <li style={{ ...listItemStyle, color: "var(--text-dim)", cursor: "default" }}>
            No matches
          </li>
        </ul>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 28px 8px 10px",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  color: "var(--text)",
  fontSize: "0.9rem",
  fontFamily: "inherit",
};

const clearStyle: React.CSSProperties = {
  position: "absolute",
  right: 6,
  top: "50%",
  transform: "translateY(-50%)",
  background: "transparent",
  border: "none",
  color: "var(--text-dim)",
  cursor: "pointer",
  fontSize: "1rem",
  lineHeight: 1,
  padding: 2,
};

const listStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: 2,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  listStyle: "none",
  padding: 4,
  maxHeight: 220,
  overflowY: "auto",
  zIndex: 10,
};

const listItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "6px 10px",
  borderRadius: 3,
  cursor: "pointer",
  fontSize: "0.85rem",
};
